import styles from "./Garally.module.css";
import { GarallyHeader } from "./GarallyHeader";
import React from "react";
import { Button } from "./Button";
import close from "../assets/close.png";

type ImageItem = string;

type MovieItem = string;

type Props = {
  plan: string;
  price: string;
  subtitle?: string;
  color: string;
  body: string;
  images: ImageItem[];
  originalImages: ImageItem[]; // クリック時に表示する元画像
  firstImageCount?: number; // 25/120用
  movies?: MovieItem[]; // 150用
  headerIndex: number;
};

export const GarallyCommon: React.FC<Props> = (props) => {
  const [typedImages, setTypedImages] = React.useState<
    { type: "landscape" | "portrait"; img: ImageItem }[]
  >([]);
  const [visibleCount, setVisibleCount] = React.useState(
    props.firstImageCount ?? 5
  );
  const [prevCount, setPrevCount] = React.useState(props.firstImageCount ?? 5);
  // 画像ビューワー用
  const [viewerOpen, setViewerOpen] = React.useState(false);
  const [viewerImg, setViewerImg] = React.useState<string | null>(null);

  // originalImagesを事前にプリロード
  React.useEffect(() => {
    props.originalImages.forEach((img) => {
      const image = new window.Image();
      image.src = img;
    });
  }, [props.originalImages]);

  // GarallyHeaderと画像リストをフラット化
  const flatImages = React.useMemo(() => {
    const arr: (ImageItem | { header: true })[] = [];
    const firstPortraitIdx = typedImages.findIndex(
      (item) => item.type === "portrait"
    );
    let firstPortraitImg: ImageItem | null = null;
    if (firstPortraitIdx !== -1) {
      firstPortraitImg = typedImages[firstPortraitIdx].img;
    }
    arr.push({ header: true });
    if (firstPortraitImg) arr.push(firstPortraitImg);
    typedImages.forEach((item, idx) => {
      if (item.type === "portrait" && idx === firstPortraitIdx) return;
      arr.push(item.img);
    });
    return arr;
  }, [typedImages]);

  React.useEffect(() => {
    const imgPromises = props.images.map(
      (img) =>
        new Promise<{ type: "landscape" | "portrait"; img: ImageItem }>(
          (resolve) => {
            const image = new window.Image();
            image.src = img;
            image.onload = () => {
              const type =
                image.naturalWidth > image.naturalHeight
                  ? "landscape"
                  : "portrait";
              resolve({ type, img });
            };
          }
        )
    );
    Promise.all(imgPromises).then((results) => {
      setTypedImages(results);
    });
  }, [props.images]);

  // GarallyHeader+最初の画像は必ずセットで1行目に表示し、残りはportrait画像は2カラムグリッド、landscape画像は1カラムで表示
  const renderImages = () => {
    const showImages = flatImages.slice(0, visibleCount);
    const gridItems = [];
    // 1行目: GarallyHeaderと最初の画像の順番をprops.headerIndexで切り替え
    if (
      showImages.length > 1 &&
      (showImages[0] as any).header &&
      typeof showImages[1] === "string"
    ) {
      gridItems.push(
        <div key="garally-header-row" className={styles.grid}>
          {props.headerIndex === 0 ? (
            <>
              <div className={styles.gridItem}>
                <GarallyHeader
                  plan={props.plan}
                  price={props.price}
                  color={props.color}
                  body={props.body}
                  subtitle={props.subtitle}
                />
              </div>
              <div className={styles.gridItem}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: props.movies ? "32px" : 0,
                    width: "100%",
                  }}
                >
                  <img
                    src={showImages[1]}
                    className={
                      styles.image +
                      (prevCount <= 1 && visibleCount > 1
                        ? " " + styles.fadeInSlide
                        : "")
                    }
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      let imgIndex = -1;
                      if (typeof showImages[1] === "string")
                        imgIndex = props.images.indexOf(showImages[1]);
                      if (imgIndex !== -1 && props.originalImages[imgIndex]) {
                        setViewerImg(props.originalImages[imgIndex]);
                        setViewerOpen(true);
                      }
                    }}
                    alt=""
                  />
                  {props.movies && props.movies[0] && (
                    <video
                      src={props.movies[0]}
                      loop
                      autoPlay
                      muted
                      playsInline
                      className={styles.image}
                      style={{ width: "100%" }}
                    />
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.gridItem}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: props.movies ? "32px" : 0,
                    width: "100%",
                  }}
                >
                  <img
                    src={showImages[1]}
                    className={
                      styles.image +
                      (prevCount <= 1 && visibleCount > 1
                        ? " " + styles.fadeInSlide
                        : "")
                    }
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      let imgIndex = -1;
                      if (typeof showImages[1] === "string")
                        imgIndex = props.images.indexOf(showImages[1]);
                      if (imgIndex !== -1 && props.originalImages[imgIndex]) {
                        setViewerImg(props.originalImages[imgIndex]);
                        setViewerOpen(true);
                      }
                    }}
                    alt=""
                  />
                  {props.movies && props.movies[0] && (
                    <video
                      src={props.movies[0]}
                      loop
                      muted
                      className={styles.image}
                      style={{ width: "100%" }}
                    />
                  )}
                </div>
              </div>
              <div className={styles.gridItem}>
                <GarallyHeader
                  plan={props.plan}
                  price={props.price}
                  color={props.color}
                  body={props.body}
                  subtitle={props.subtitle}
                />
              </div>
            </>
          )}
        </div>
      );
    }
    // 2行目以降: landscapeは1カラム、portraitは2カラムグリッド
    const restImages = showImages.slice(2);
    let portraitBuffer: { img: ImageItem; idx: number }[] = [];
    restImages.forEach((img, idx) => {
      const globalIdx = idx + 2;
      // 画像の型をtypedImages.findIndexで取得
      const originalIdx = typedImages.findIndex((ti) => ti.img === img);
      const type =
        originalIdx !== -1 ? typedImages[originalIdx].type : "portrait";
      const isNew = prevCount <= globalIdx && visibleCount > globalIdx;
      if (type === "landscape") {
        if (portraitBuffer.length > 0) {
          gridItems.push(
            <div key={`portrait-row-${globalIdx}`} className={styles.grid}>
              {portraitBuffer.map(({ img: pimg, idx: pi }) => (
                <div key={pimg} className={styles.gridItem}>
                  <img
                    src={pimg}
                    className={
                      styles.image +
                      (prevCount <= pi && visibleCount > pi
                        ? " " + styles.fadeInSlide
                        : "")
                    }
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      const imgIndex = props.images.indexOf(pimg);
                      if (imgIndex !== -1 && props.originalImages[imgIndex]) {
                        setViewerImg(props.originalImages[imgIndex]);
                        setViewerOpen(true);
                      }
                    }}
                    alt=""
                  />
                </div>
              ))}
            </div>
          );
          portraitBuffer = [];
        }

        if (typeof img === "string") {
          gridItems.push(
            <div key={`landscape-${img}`} className={styles.landscapeFull}>
              <img
                src={img}
                className={
                  styles.image + (isNew ? " " + styles.fadeInSlide : "")
                }
                style={{ cursor: "pointer" }}
                onClick={() => {
                  const imgIndex = props.images.indexOf(img);
                  if (imgIndex !== -1 && props.originalImages[imgIndex]) {
                    setViewerImg(props.originalImages[imgIndex]);
                    setViewerOpen(true);
                  }
                }}
                alt=""
              />
            </div>
          );
        }
      } else {
        if (typeof img === "string") {
          portraitBuffer.push({ img, idx: globalIdx });
        }
        if (portraitBuffer.length === 2) {
          gridItems.push(
            <div key={`portrait-row-${globalIdx}`} className={styles.grid}>
              {portraitBuffer.map(({ img: pimg, idx: pi }) => (
                <div key={pimg} className={styles.gridItem}>
                  <img
                    src={pimg}
                    className={
                      styles.image +
                      (prevCount <= pi && visibleCount > pi
                        ? " " + styles.fadeInSlide
                        : "")
                    }
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      const imgIndex = props.images.indexOf(pimg);
                      if (imgIndex !== -1 && props.originalImages[imgIndex]) {
                        setViewerImg(props.originalImages[imgIndex]);
                        setViewerOpen(true);
                      }
                    }}
                    alt=""
                  />
                </div>
              ))}
            </div>
          );
          portraitBuffer = [];
        }
      }
    });
    if (portraitBuffer.length > 0) {
      gridItems.push(
        <div key={`portrait-row-last`} className={styles.grid}>
          {portraitBuffer.map(({ img: pimg, idx: pi }) => (
            <div key={pimg} className={styles.gridItem}>
              <img
                src={pimg}
                className={
                  styles.image +
                  (prevCount <= pi && visibleCount > pi
                    ? " " + styles.fadeInSlide
                    : "")
                }
                style={{ cursor: "pointer" }}
                onClick={() => {
                  const imgIndex = props.images.indexOf(pimg);
                  if (imgIndex !== -1 && props.originalImages[imgIndex]) {
                    setViewerImg(props.originalImages[imgIndex]);
                    setViewerOpen(true);
                  }
                }}
                alt=""
              />
            </div>
          ))}
        </div>
      );
    }
    return <>{gridItems}</>;
  };

  const hasMore = visibleCount < flatImages.length;

  return (
    <>
      <div className={styles.container}>
        {renderImages()}
        {hasMore && (
          <div
            style={{ marginTop: 32, display: "flex", justifyContent: "center" }}
          >
            <Button
              onPress={() => {
                setPrevCount(visibleCount);
                setVisibleCount((c) => c + 2);
              }}
            />
          </div>
        )}
      </div>
      {/* viewerOpen: trueでopenクラス, falseでcloseクラスを付与 */}
      <div
        className={
          styles.viewerModal +
          (viewerOpen && viewerImg ? " " + styles.open : " " + styles.close)
        }
        style={{ pointerEvents: viewerOpen && viewerImg ? "auto" : "none" }}
        onClick={() => {
          viewerOpen && setViewerOpen(false);
          setViewerImg(null);
        }}
      >
        <div className={styles.modalContent}>
          <img
            src={viewerImg ?? ""}
            alt="viewer"
            className={styles.viewerImg}
          />
          <img
            src={close}
            className={styles.closeButton}
            onClick={() => {
              setViewerOpen(false);
              setViewerImg(null);
            }}
            alt=""
          />
        </div>
      </div>
    </>
  );
};
